import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Container,
  Heading,
  HStack,
  IconButton,
  SimpleGrid,
  Text,
  Tooltip,
  useToast,
  VStack,
} from '@chakra-ui/react';
import axios from 'axios';
import React, { useState } from 'react';
import {
  FiArrowLeft,
  FiCheckCircle,
  FiClock,
  FiRefreshCw,
  FiUserCheck,
  FiUsers,
} from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';
import ReportCard from '../../components/ReportCard';
import ReportCardWithDateFilters from '../../components/ReportCardWithDateFilters';
import { useReportStats } from '../../hooks/useReportStats';
import { API_URL } from '../../api/config';
import ResumoDoEvento from '../../components/ResumoDoEvento';

const ReportsHubPage: React.FC = () => {
  const { stats, loading, refresh } = useReportStats();
  const toast = useToast();
  const [exportingAccess, setExportingAccess] = useState(false);
  const [exportingRegistrations, setExportingRegistrations] = useState(false);
  const [exportingCheckin, setExportingCheckin] = useState(false);

  const handleRefresh = () => {
    refresh();
    toast({
      title: 'Dados atualizados',
      description: 'As estatísticas foram recarregadas com sucesso',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleExportAccess = async (filters: { startDate?: string; endDate?: string }) => {
    setExportingAccess(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const queryString = params.toString();
      const url = `${API_URL}access/export-by-person${queryString ? `?${queryString}` : ''}`;

      const response = await axios.get(url, {
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      });

      const timestamp = new Date().toISOString().split('T')[0];
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `controle_acesso_por_pessoas_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);

      toast({
        title: 'Exportação concluída',
        description: 'Relatório de acessos exportado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Erro ao exportar acessos:', error);

      // Tratar resposta 404 com mensagem do backend
      if (error.response?.status === 404 && error.response?.data) {
        try {
          // Como responseType é 'blob', precisamos converter para JSON
          const text = await error.response.data.text();
          const jsonError = JSON.parse(text);

          toast({
            title: 'Nenhum registro encontrado',
            description: jsonError.message || 'Nenhum acesso encontrado no período selecionado.',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
          return;
        } catch (parseError) {
          // Se não conseguir parsear, continua com o erro genérico abaixo
        }
      }

      toast({
        title: 'Erro na exportação',
        description: 'Não foi possível exportar o relatório de acessos',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExportingAccess(false);
    }
  };

  const handleExportCheckin = async (filters: { startDate?: string; endDate?: string }) => {
    setExportingCheckin(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const queryString = params.toString();
      const url = `${API_URL}users/export-checkin${queryString ? `?${queryString}` : ''}`;

      const response = await axios.get(url, {
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'checkin_data.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);

      toast({
        title: 'Exportação concluída',
        description: 'Relatório de check-ins exportado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Erro ao exportar check-ins:', error);

      // Tratar resposta 404 com mensagem do backend
      if (error.response?.status === 404 && error.response?.data) {
        try {
          // Como responseType é 'blob', precisamos converter para JSON
          const text = await error.response.data.text();
          const jsonError = JSON.parse(text);

          toast({
            title: 'Nenhum registro encontrado',
            description: jsonError.message || 'Nenhum check-in encontrado no período selecionado.',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
          return;
        } catch (parseError) {
          // Se não conseguir parsear, continua com o erro genérico abaixo
        }
      }

      toast({
        title: 'Erro na exportação',
        description: 'Não foi possível exportar o relatório de check-ins',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExportingCheckin(false);
    }
  };

  const handleExportRegistrations = async () => {
    setExportingRegistrations(true);
    try {
      const response = await axios.get(`${API_URL}users/export-all`, {
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      });

      const timestamp = new Date().toISOString().split('T')[0];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inscricoes_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);

      toast({
        title: 'Exportação concluída',
        description: 'Relatório de inscrições exportado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Erro ao exportar inscrições:', error);

      // Tratar resposta 404 com mensagem do backend
      if (error.response?.status === 404 && error.response?.data) {
        try {
          // Como responseType é 'blob', precisamos converter para JSON
          const text = await error.response.data.text();
          const jsonError = JSON.parse(text);

          toast({
            title: 'Nenhum registro encontrado',
            description: jsonError.message || 'Nenhum participante encontrado.',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
          return;
        } catch (parseError) {
          // Se não conseguir parsear, continua com o erro genérico abaixo
        }
      }

      toast({
        title: 'Erro na exportação',
        description: 'Não foi possível exportar o relatório de inscrições',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExportingRegistrations(false);
    }
  };

  return (
    <Container maxW="full" py={8} px={{ base: 4, md: 6 }}>
      {/* Breadcrumb e Ações */}
      <HStack justify="space-between" mb={6}>
        <Breadcrumb>
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to="/admin">
              Admin
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink>Relatórios</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <HStack spacing={2}>
          <Tooltip label="Atualizar estatísticas">
            <IconButton
              aria-label="Atualizar estatísticas"
              icon={<FiRefreshCw />}
              variant="outline"
              onClick={handleRefresh}
              isLoading={loading}
            />
          </Tooltip>
          <Tooltip label="Voltar ao Dashboard">
            <IconButton
              aria-label="Voltar ao Dashboard"
              icon={<FiArrowLeft />}
              variant="outline"
              as={RouterLink}
              to="/admin"
            />
          </Tooltip>
        </HStack>
      </HStack>

      {/* Cabeçalho */}
      <VStack align="start" spacing={2} mb={8}>
        <Heading size="lg">Central de Relatórios</Heading>
        <Text color="gray.500">
          Visualize e exporte relatórios de acessos, inscrições e check-ins
        </Text>
      </VStack>

      {/* Grid de Cards de Relatórios */}
      <ResumoDoEvento />

      <SimpleGrid columns={{ base: 1, md: 2, lg: 2, xl: 4 }} spacing={6}>
        {/* Card de Participantes */}
        <ReportCard
          title="Participantes"
          description="Todos os cadastros (com ou sem check-in)"
          icon={FiUserCheck}
          iconColor="green.500"
          statValue={stats.totalRegistrations.toLocaleString('pt-BR')}
          statLabel="Total de Participantes"
          statHelpText="Todos os cadastros realizados"
          viewLink="/admin/registration-list"
          onExport={handleExportRegistrations}
          isLoading={loading}
          isExporting={exportingRegistrations}
        />

        {/* Card de Check-ins */}
        <ReportCardWithDateFilters
          title="Check-ins Realizados"
          description="Confirmações de presença no evento"
          icon={FiCheckCircle}
          iconColor="purple.500"
          statValue={stats.totalCheckins.toLocaleString('pt-BR')}
          statLabel="Total de Check-ins"
          statHelpText={`${stats.totalCheckinsToday} check-ins hoje`}
          onExport={handleExportCheckin}
          isLoading={loading}
          isExporting={exportingCheckin}
        />

        {/* Card de Acessos */}
        <ReportCardWithDateFilters
          title="Controle de Acessos"
          description="Relatório detalhado de entradas e saídas"
          icon={FiUsers}
          iconColor="blue.500"
          statValue={stats.totalAccesses.toLocaleString('pt-BR')}
          statLabel="Total de Acessos"
          statHelpText="Registros de entrada e saída"
          viewLink="/admin/access-list"
          onExport={handleExportAccess}
          isLoading={loading}
          isExporting={exportingAccess}
        />

        {/* Card de Permanência (futuro) */}
        {/* <ReportCard
          title="Tempo de Permanência"
          description="Análise de tempo médio no evento"
          icon={FiClock}
          iconColor="orange.500"
          statValue={stats.averageStayTime || 'N/A'}
          statLabel="Tempo Médio"
          statHelpText="Em desenvolvimento"
          isLoading={loading}
        /> */}
      </SimpleGrid>
    </Container>
  );
};

export default ReportsHubPage;
